import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import isNil from 'lodash/isNil';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import GlobalSearchResultItem from '@/components/global-search/GlobalSearchResultItem';
import { SearchResultItem } from '@/components/global-search/type';
import { getGroupLabel, getSecondaryContent, getSortedGroupKeys, groupedData } from '@/components/global-search/utils';
import { useSession } from '@/components/session/useSession';
import { useGlobalSearchContext } from '@/contexts/GlobalSearchContext';
import { highlightText } from '@/utils/highlightText';

export default function GlobalSearchResults() {
  const selectedItemRef = useRef<HTMLAnchorElement>(null);
  const {
    org: { orgSector },
  } = useSession();

  const { state, send } = useGlobalSearchContext();
  const searchQuery = state.context.query;
  const selectedIndex = state.context.selectedResultItemIndex;
  const selectedNavigationOptionIndex = state?.context.selectedResultItemNavigationActionIndex;
  const results = state.context.results;
  const selectedTab = state?.context.tabs.at(state?.context.selectedTabIndex ?? 0);

  useLayoutEffect(() => {
    if (selectedItemRef.current && !isNil(selectedIndex)) {
      selectedItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  const showDividers = selectedTab === 'all';

  const filteredResults = useMemo(() => {
    if (!selectedTab) return results;
    if (selectedTab === 'all') {
      return results;
    }
    return results.filter((item) => item.type === selectedTab);
  }, [results, selectedTab]);

  const groupedItems = useMemo(() => {
    if (!showDividers) {
      return null;
    }
    return groupedData(filteredResults);
  }, [filteredResults, showDividers]);

  const sortedGroupKeys = useMemo(() => {
    if (!groupedItems) return [];
    return getSortedGroupKeys(groupedItems);
  }, [groupedItems]);

  const highlightCustomerIdentifier = (item: SearchResultItem) => {
    if (
      (item.type === 'building' || item.type === 'building-id' || item.type === 'action-plan') &&
      item.customerExternalIdentifier
    ) {
      return highlightText(item.customerExternalIdentifier, searchQuery);
    }
    return undefined;
  };

  const renderItem = (item: SearchResultItem, itemIndex: number, globalIndex: number) => {
    const highlightedTitle = highlightText(item.title, searchQuery);
    const isSelected = selectedIndex === globalIndex && !isNil(selectedIndex);
    const rawSecondaryContent = getSecondaryContent(item, orgSector);
    const secondaryContent =
      !isNil(rawSecondaryContent) && rawSecondaryContent.length > 0
        ? highlightText(rawSecondaryContent, searchQuery)
        : undefined;
    const key =
      item.type === 'building' || item.type === 'building-id'
        ? `${item.type}-${item.buildingId}-${itemIndex}`
        : `${item.type}-${item.title}-${item.path}-${itemIndex}`;

    return (
      <GlobalSearchResultItem
        title={highlightedTitle}
        key={key}
        isSelected={isSelected}
        ref={isSelected ? selectedItemRef : undefined}
        displayBuildingPath={secondaryContent}
        customerExternalIdentifier={highlightCustomerIdentifier(item)}
        selectedNavigationOptionIndex={isSelected ? selectedNavigationOptionIndex : 0}
        item={item}
        send={send}
        query={searchQuery}
      />
    );
  };

  if (showDividers && groupedItems) {
    let globalIndex = 0;
    return (
      <List disablePadding sx={{ px: 2 }}>
        {sortedGroupKeys.map((groupKey, groupIndex) => {
          const groupItems = groupedItems[groupKey] || [];
          const firstItem = groupItems[0];
          const sectionTitle = firstItem ? getGroupLabel(firstItem.type, orgSector) : groupKey;
          return (
            <div key={groupKey}>
              {groupIndex > 0 && <Divider sx={{ my: 1 }} />}
              <PTypography
                variant={'overline'}
                color="secondary"
                sx={{
                  letterSpacing: 0.5,
                  px: 2,
                  py: 1,
                  display: 'block',
                }}
              >
                {sectionTitle}
              </PTypography>
              {groupItems.map((item, itemIndex) => {
                const rendered = renderItem(item, itemIndex, globalIndex);
                globalIndex++;
                return rendered;
              })}
            </div>
          );
        })}
      </List>
    );
  }

  return (
    <List disablePadding sx={{ px: 2 }}>
      {filteredResults.map((item, itemIndex) => renderItem(item, itemIndex, itemIndex))}
    </List>
  );
}
